import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Debounced value hook — the tried-and-true `useDebouncedValue`.
 *
 * Yields the latest `value` after it has remained unchanged for `delayMs`.
 * Useful for passing search-input text to a TanStack Query key so the
 * network request only fires on a paused typing burst, not per keystroke.
 *
 * Clears the pending timeout on every update and on unmount — no
 * fire-after-unmount hazard.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

/**
 * Debounced callback hook — stable identity, always calls the latest `fn`.
 *
 * The returned function schedules `fn(...args)` to run `delayMs` after the
 * *last* call. Calls that arrive inside the window cancel the prior schedule.
 * The returned function's identity is stable across renders (safe to use as
 * a useEffect dep or pass to memoised children); the actual `fn` body is
 * read from a ref at flush time, so closure-over-state works as expected.
 *
 * Clears on unmount to avoid stale dispatches after the component is gone.
 *
 * Also exposes `.cancel()` and `.flush()` for call sites that need tighter
 * control (e.g. flushing an in-flight save on form submit).
 */
export interface DebouncedCallback<A extends unknown[]> {
  (...args: A): void
  cancel: () => void
  flush: () => void
}

export function useDebouncedCallback<A extends unknown[]>(
  fn: (...args: A) => void,
  delayMs: number,
): DebouncedCallback<A> {
  const fnRef = useRef(fn)
  useEffect(() => { fnRef.current = fn }, [fn])

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingArgsRef = useRef<A | null>(null)

  const cancel = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    pendingArgsRef.current = null
  }, [])

  const flush = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    const args = pendingArgsRef.current
    pendingArgsRef.current = null
    if (args) fnRef.current(...args)
  }, [])

  // Clean up timer on unmount so a pending flush doesn't call a stale
  // setState / navigation handler after the component is gone.
  useEffect(() => cancel, [cancel])

  const debounced = useCallback((...args: A) => {
    pendingArgsRef.current = args
    if (timerRef.current != null) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      const pending = pendingArgsRef.current
      pendingArgsRef.current = null
      if (pending) fnRef.current(...pending)
    }, delayMs)
  }, [delayMs]) as DebouncedCallback<A>

  debounced.cancel = cancel
  debounced.flush = flush
  return debounced
}
